import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import styles from "../Components/Products/Product.module.css";
import CardPlaceholder from "../Components/UILibrary/CardPlaceholder";
import CategorySection from "../Components/Products/CategorySection";

import type { ICategoryData, IProductCardProps } from "@model/Products.model";
import { CategoriesService } from "../../Logic/CategoriesService";
import { CategoriesMeta } from "@model/Category.model";

const OurProductsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Number(searchParams.get("page") || 1);
  const perPage = Number(searchParams.get("perPage") || 10);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [categories, setCategories] = useState<ICategoryData[]>([]);
  const [meta, setMeta] = useState<CategoriesMeta>({
    total: 0,
    page: currentPage,
    perPage,
    pages: 1,
  });

  const categoriesService = useMemo(() => new CategoriesService(), []);

  useEffect(() => {
    let isMounted = true;
    categoriesService
      .handleGetCategoriesWithProducts({
        page: currentPage,
        perPage,
        setLoading: (v) => isMounted && setLoading(v),
        setApiError: (m) => isMounted && setApiError(m),
        setItems: (rows) => isMounted && setCategories(rows),
        setMeta: (m) =>
          isMounted &&
          setMeta({
            total: m?.total ?? 0,
            page: m?.page ?? currentPage,
            perPage: m?.perPage ?? perPage,
            pages: m?.pages ?? 1,
          }),
      })
      .catch(() => {});

    return () => {
      isMounted = false;
    };
  }, [categoriesService, currentPage, perPage, searchParams]);

  const handlePageChange = (direction: "prev" | "next") => {
    const targetPage = categoriesService.getTargetPage(meta, direction);

    categoriesService.goToPage({
      targetPage,
      meta,
      loading,
      searchParams,
      perPage,
      setSearchParams,
    });
  };

  return (
    <main className={styles.ourNoRules} data-testid="our-products-page">
      <h1 className="margin-left-15" data-testid="our-products-title">
        Our Products
      </h1>

      {loading ? (
        <div className={styles.ourLoading} data-testid="loading-placeholder">
          <CardPlaceholder count={8} />
        </div>
      ) : apiError ? (
        <p role="alert" className={styles.ourError}>
          {apiError}
        </p>
      ) : categories.length === 0 ? (
        <p className={styles.ourNoResults} data-testid="our-products-empty">
          No items were found.
        </p>
      ) : (
        <Fragment>
          <div data-testid="categories-list">
            {categories.map((cat, idx) => (
              <CategorySection
                key={cat.id}
                title={cat.title}
                description={cat.description}
                products={cat.products as IProductCardProps[]}
                defaultExpanded={idx === 0}
              />
            ))}
          </div>

          <div className={styles.pagination} data-testid="our-products-pagination">
            <button
              type="button"
              onClick={() => handlePageChange("prev")}
              disabled={meta.page <= 1 || loading}
              className={styles.paginationButton}
            >
              Previous
            </button>

            <span className={styles.paginationInfo}>
              Page <strong>{meta.page}</strong> of <strong>{meta.pages}</strong>
            </span>

            <button
              type="button"
              onClick={() => handlePageChange("next")}
              disabled={meta.page >= meta.pages || loading}
              className={styles.paginationButton}
            >
              Next
            </button>
          </div>
        </Fragment>
      )}
    </main>
  );
};

export default OurProductsPage;
